import tokml from "geojson-to-kml";
import md5 from "md5";
import { Partitioner } from "./Partitioner";
import { GeoJSONGeometryNoCollections, Tornado } from "./tornado";
import { colorBlend, separateGeometryCollections } from "./utils";


const prettysources = (sources: (string | Record<string, string | null>)[] | undefined, add_links: boolean) => {
    if (sources === undefined) {
        return "";
    }

    let additional_idx = -1;
    let elems = sources.map((src_obj, idx) => {
        const link_txt = Object.keys(src_obj)[0];
        const link_href = Object.values(src_obj)[0];

        if (link_txt == 'Source 1') {
            additional_idx = idx;
        }

        if (add_links) {
            return link_href === null ? link_txt : `<a href='${link_href}' target='_blank'>${link_txt}</a>`;
        }
        else {
            return link_href === null ? link_txt : `${link_txt} (${link_href})`;
        }
    });

    elems = elems.map((elem, idx) => idx == 0 ? elem : [', ', elem]).flat();
    if (additional_idx > 0) {
        elems.splice(2 * additional_idx, 0, 'Additional: ');
    }
    return elems.join("");
}


function downloadFile(file_name: string, data: string) {
    let link = document.createElement('a');
    link.download = file_name
    link.href = 'data:Application/octet-stream,' + encodeURIComponent(data);
    link.click();
}


function downloadKML(tornadoes: Tornado[], partitioner: Partitioner) {

    let tornadoes_sep = separateGeometryCollections(tornadoes) as GeoJSON.Feature[];
    tornadoes_sep.sort((a, b) => {
        let a_props: GeoJSON.GeoJsonProperties = a['properties'];
        let b_props: GeoJSON.GeoJsonProperties = b['properties'];

        const a_rating = a_props === null ? -9 : a_props['f-scale'];
        const b_rating = b_props === null ? -9 : b_props['f-scale'];
        const a_line_boost = a['geometry']['type'] == 'LineString' ? 10 : 0;
        const b_line_boost = b['geometry']['type'] == 'LineString' ? 10 : 0;
        return (a_rating + a_line_boost) - (b_rating + b_line_boost);
    });

    tornadoes_sep = tornadoes_sep.map(tor => {
        tor = Object.assign({}, tor);
        let tor_props = Object.assign({}, tor['properties']);
        if (tor_props === null) {
            tor_props = {};
        }

        const scale = tor_props['scale'];
        const rating = tor_props['f-scale'] == -9 ? 'U' : `${tor_props['f-scale']}`;
        const id = tor_props['id'];

        const part_key_idx = partitioner.part_keys.map(pk => pk.slice(-1)).indexOf(rating);

        tor_props['source'] = prettysources(tor_props['source'], true);

        switch (tor['geometry']['type']) {
            case 'LineString':
                tor_props['name'] = `${id} (${scale}${rating} Tornado Track)`;
                tor_props['stroke'] = partitioner.colors[part_key_idx];
                tor_props['stroke-width'] = 6;
                tor_props['path-length'] = `${Math.round(tor_props['path-length'] * 10) / 10} mi`;
                tor_props['path-width'] = `${tor_props['path-width']} yds`;
                break;
            case 'Polygon':
                const poly_opacity = 0.5;
                const poly_opacity_hex = Math.round(poly_opacity * 255).toString(16).padStart(2, '0');
                const poly_color = colorBlend(partitioner.colors[part_key_idx] + poly_opacity_hex, '#818888ff').slice(0, -2);

                tor_props['name'] = `${id} (${scale}${rating} Tornado Damage Swath)`;
                tor_props['fill'] = poly_color;
                tor_props['stroke-width'] = 0;
                ['datetime-start', 'path-length', 'path-width', 'fatalities', 'injuries'].forEach(p => { if (tor_props !== null) delete tor_props[p]; });
                break;
            case 'Point':
                tor_props['name'] = `${id} (${scale}${rating} Tornado)`;
                break;
        }

        tor['properties'] = tor_props;
        return tor;
    });

    const kml = tokml({
        'type': 'FeatureCollection',
        'features': tornadoes_sep
    }, {'simplestyle': true, 'name': 'name'});

    const digest = md5(JSON.stringify(tornadoes));
    const file_name = `${digest}.kml`;

    downloadFile(file_name, kml);
}

function downloadCSV(tornadoes: Tornado[]) {
    const column_headers = ['id', 'countries', 'states', 'datetime-start', 'start-lat', 'start-lon', 'end-lat', 'end-lon', 
                            'rating', 'path-length', 'path-width', 'injuries', 'fatalities', 'comments', 'source'];
    const column_names: Record<string, string> = {'id': 'ID', 'countries': 'Countries', 'states': 'States', 'datetime-start': 'Start Time', 
                                                  'start-lat': 'Start Latitude', 'start-lon': 'Start Longitude', 'end-lat': 'End Latitude', 'end-lon': 'End Longitude',
                                                  'rating': 'Rating', 'path-length': 'Path Length (mi)', 'path-width': 'Path Width (yds)',
                                                  'injuries': 'Injuries', 'fatalities': 'Fatalities', 'comments': 'Comments', 'source': 'Sources'};

    const csv_data = tornadoes.map(tor => {
        const tor_props = tor['properties'] as Record<string, any>;

        let tor_geom: GeoJSONGeometryNoCollections | GeoJSON.Polygon;
        if (tor['geometry']['type'] == 'Point' || tor['geometry']['type'] == 'LineString') {
            tor_geom = tor['geometry'];
        }
        else if (tor['geometry']['type'] == 'GeometryCollection') {
            tor_geom = tor['geometry']['geometries'][0];
        }

        return column_headers.map(hdr => {
            if (hdr == 'countries' || hdr == 'states') {
                return (tor_props[hdr] === undefined ? '' : tor_props[hdr].join(';')) as string;
            }
            else if (hdr == 'comments') {
                return (tor_props[hdr] === undefined ? '' : `"${tor_props[hdr]}"`) as string;
            }
            else if (hdr == 'source') {
                return prettysources(tor_props['source'], false).replaceAll(',', ';');
            }
            else if (hdr in tor['properties']) {
                return (tor_props[hdr] === undefined ? '' : tor_props[hdr]) as string;
            }
            else if (hdr == 'rating') {
                const rating = Array.isArray(tor_props['f-scale']) ? tor_props['f-scale'][0] : tor_props['f-scale'];
                return tor_props['scale'] + (rating == -9 ? 'U' : rating) as string;
            }
            else if (hdr == 'start-lat') {
                const lat = (tor_geom['type'] == 'Point' ? tor_geom['coordinates'][1] : tor_geom['coordinates'][0][1]) as number;
                return Math.round(lat * 1000) / 1000;
            }
            else if (hdr == 'start-lon') {
                const lon = (tor_geom['type'] == 'Point' ? tor_geom['coordinates'][0] : tor_geom['coordinates'][0][0]) as number;
                return Math.round(lon * 1000) / 1000;
            }
            else if (hdr == 'end-lat') {
                const lat = (tor_geom['type'] == 'Point' ? tor_geom['coordinates'][1] : tor_geom['coordinates'][tor_geom['coordinates'].length - 1][1]) as number;
                return Math.round(lat * 1000) / 1000;
            }
            else if (hdr == 'end-lon') {
                const lon = (tor_geom['type'] == 'Point' ? tor_geom['coordinates'][0] : tor_geom['coordinates'][tor_geom['coordinates'].length - 1][0]) as number;
                return Math.round(lon * 1000) / 1000;
            }
        }).join(",")
    }).join("\n");

    const digest = md5(JSON.stringify(tornadoes));
    const file_name = `${digest}.csv`;

    downloadFile(file_name, column_headers.map(hdr => column_names[hdr]).join(",") + '\n' + csv_data);
}

function downloadGeoJSON(tornadoes: Tornado[]) {
    const tornadoes_str = JSON.stringify(tornadoes, undefined, 4);
    const digest = md5(JSON.stringify(tornadoes));
    const file_name = `${digest}.json`;
    downloadFile(file_name, tornadoes_str);
}

export {downloadKML, downloadCSV, downloadGeoJSON}